import React, {useRef} from "react";
import {Link as ReactLink, useHistory} from "react-router-dom";
import {useKeycloak} from "@react-keycloak/web";
import {useTranslation} from "react-i18next";
import i18n from "i18next";
// Material UI imports
import {
    Box,
    Chip,
    Container,
    FormControl,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
} from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";
// Project imports
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import SearchIcon from "@material-ui/icons/Search";
import {ServerCommunicationAlert} from "../../common/errors";
import {Loading} from "../../common/Loading";
import {MunicipalityListItemDTO, MunicipalityType, municipalityTypes} from "../../../models/organization";
import API, {createApiConfig} from "../../../utils/API";
import {AxiosError} from "axios";
import ROUTES from "../../../routes/routes";
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import {Alert} from "@material-ui/lab";
import {Page} from "../../../models/pagination";
import {ManualSidebar} from "../../../manual/components/ManualSidebar";
import {csPublicSearchSection} from "../../../manual/content/cs/CsPublicManualChapter";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        visuallyHidden: {
            border: 0,
            clip: "rect(0 0 0 0)",
            height: 1,
            margin: -1,
            overflow: "hidden",
            padding: 0,
            position: "absolute",
            top: 20,
            width: 1,
        },
        avatarBox: {
            width: 40,
            height: 40,
            borderRadius: "8px",
            backgroundColor: "#2563EB",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            flexShrink: 0,
        },
        avatarImg: {
            width: "100%",
            height: "100%",
            objectFit: "contain",
        },
        avatarInitials: {
            color: "#ffffff",
            fontSize: "0.75rem",
            fontWeight: 700,
            textTransform: "uppercase" as const,
            lineHeight: 1,
            textAlign: "center" as const,
        },
        tableRow: {
            cursor: "pointer",
            "&:hover": {
                backgroundColor: "#F9FAFB",
            },
        },
        nameLink: {
            color: "#2563EB",
            fontWeight: 600,
            textDecoration: "none",
            "&:hover": {
                textDecoration: "underline",
            },
        },
        typeChip: {
            backgroundColor: "#EFF6FF",
            color: "#2563EB",
            fontWeight: 500,
            fontSize: "0.75rem",
            height: "24px",
        },
        chevron: {
            color: "#9CA3AF",
        },
        searchWrapper: {
            marginBottom: theme.spacing(2),
        },
        searchInput: {
            backgroundColor: "#ffffff",
            borderRadius: "8px",
        },
        filterWrapper: {
            display: "flex",
            flexWrap: "wrap" as const,
            gap: theme.spacing(2),
            alignItems: "center",
            marginBottom: theme.spacing(3),
        },
        filterLabel: {
            fontSize: "0.8125rem",
            fontWeight: 500,
            color: "#6B7280",
            whiteSpace: "nowrap" as const,
        },
        filterGroup: {
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(1),
            flexWrap: "wrap" as const,
        },
        defaultTypeChip: {
            backgroundColor: "#ffffff",
            color: "#374151",
            fontWeight: 400,
            fontSize: "0.75rem",
            height: "28px",
            cursor: "pointer",
            border: "1px solid #E5E7EB",
        },
        activeTypeChip: {
            backgroundColor: "#EFF6FF",
            color: "#2563EB",
            fontWeight: 500,
            fontSize: "0.75rem",
            height: "28px",
            cursor: "pointer",
            border: "1px solid #BFDBFE",
        },
        inactiveTypeChip: {
            backgroundColor: "#F3F4F6",
            color: "#9CA3AF",
            fontWeight: 400,
            fontSize: "0.75rem",
            height: "28px",
            cursor: "pointer",
            border: "1px solid #E5E7EB",
        },
        krajSelect: {
            backgroundColor: "#ffffff",
            borderRadius: "8px",
            minWidth: "200px",
            "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
            },
        },
        tableContainer: {
            borderRadius: "12px",
            border: "1px solid #E5E7EB",
            overflow: "hidden",
        },
        paginationWrapper: {
            display: "flex",
            justifyContent: "center",
            padding: theme.spacing(2),
            gap: theme.spacing(1),
        },
        prevNextBtn: {
            borderRadius: "8px",
            border: "1px solid #E5E7EB",
            padding: "6px 16px",
            backgroundColor: "#ffffff",
            cursor: "pointer",
            fontSize: "0.875rem",
            fontWeight: 500,
            color: "#374151",
            "&:hover": {
                backgroundColor: "#F9FAFB",
            },
        },
    })
);

function getInitials(name: string): string {
    const words = name.trim().split(/\s+/);
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
}

type SortableProperties = "name" | "type" | "county_cz" | "region_cz" | "area" | "population" | "active";

const propertiesWithDefaultDesc = ["type", "area", "population"] as SortableProperties[];

interface MunicipalityRowProps {
    municipality: MunicipalityListItemDTO;
    onClick: () => void;
}

const Municipality: React.FC<MunicipalityRowProps> = (props: MunicipalityRowProps) => {
    const [t] = useTranslation("organization");
    const classes = useStyles();
    const logoSrc = props.municipality.hasLogo
        ? API.defaults.baseURL + "/organizations/" + props.municipality.publicId + "/logo"
        : null;

    return (
        <TableRow className={classes.tableRow} onClick={props.onClick} hover>
            <TableCell>
                <Box className={classes.avatarBox}>
                    {logoSrc ? (
                        <img src={logoSrc} alt={t("organization.logo")} className={classes.avatarImg} />
                    ) : (
                        <span className={classes.avatarInitials}>{getInitials(props.municipality.name)}</span>
                    )}
                </Box>
            </TableCell>
            <TableCell>
                <ReactLink
                    to={ROUTES.organization.create(props.municipality.publicId)}
                    className={classes.nameLink}
                    onClick={(e) => e.stopPropagation()}
                >
                    {props.municipality.name}
                </ReactLink>
            </TableCell>
            <TableCell>
                <Chip
                    label={t("municipalityType." + props.municipality.type.toUpperCase())}
                    className={classes.typeChip}
                    size="small"
                />
            </TableCell>
            <TableCell>
                {props.municipality?.type === "KRAJ"
                    ? null
                    : i18n.exists("organization:county." + props.municipality?.territorialCode.substring(0, 6))
                    ? t("county." + props.municipality.territorialCode.substring(0, 6))
                    : t("placeholders.unknown")}
            </TableCell>
            <TableCell>
                {(() => {
                    const isKraj = props.municipality?.type === "KRAJ";
                    const regionKey = isKraj
                        ? props.municipality?.territorialCode
                        : props.municipality?.territorialCode.substring(0, 5);
                    return regionKey && i18n.exists("organization:region." + regionKey)
                        ? t("region." + regionKey)
                        : t("placeholders.unknown");
                })()}
            </TableCell>
            <TableCell align="right" style={{fontVariantNumeric: "tabular-nums"}}>
                {props.municipality.population.toLocaleString("cs-CZ")}
            </TableCell>
            <TableCell align="right" style={{fontVariantNumeric: "tabular-nums"}}>
                <span>
                    <span>{props.municipality.areaKm2.toLocaleString("cs-CZ")}</span>
                    <span>
                        &nbsp;km<span style={{verticalAlign: "super", fontSize: "0.65em"}}>2</span>
                    </span>
                </span>
            </TableCell>
            <TableCell align="right">
                <ChevronRightIcon className={classes.chevron} fontSize="small" />
            </TableCell>
        </TableRow>
    );
};

interface SortableHeaderProps {
    label: string;
    orderedBy: SortableProperties;
    orderedDirection: "asc" | "desc";
    align?: "left" | "center" | "right" | "justify" | "inherit";
    property: SortableProperties;
    handler: (property: SortableProperties) => void;
    classOfHidden?: string;
}

function SortableHeader(props: SortableHeaderProps) {
    const orderedByThisColumn = props.orderedBy === props.property;
    return (
        <TableCell align={props.align ?? "left"} style={{whiteSpace: "nowrap"}}>
            <TableSortLabel
                active={orderedByThisColumn}
                direction={
                    orderedByThisColumn
                        ? props.orderedDirection
                        : propertiesWithDefaultDesc.includes(props.property)
                        ? "desc"
                        : "asc"
                }
                onClick={() => props.handler(props.property)}
            >
                {props.label}
                {orderedByThisColumn ? (
                    <span className={props.classOfHidden}>{props.orderedDirection === "desc" ? "▼" : "▲"}</span>
                ) : null}
            </TableSortLabel>
        </TableCell>
    );
}

const Municipalities: React.FC = () => {
    const [t] = useTranslation("organization");
    const {keycloak, initialized} = useKeycloak();
    const history = useHistory();
    const initState = {
        page: {
            size: 50,
            totalElements: 0,
            totalPages: 0,
            number: 0,
        },
        searchString: "",
        municipalities: [] as MunicipalityListItemDTO[],
        orderBy: "active" as SortableProperties,
        orderDirection: "desc" as "asc" | "desc",
        name: "loading" as "loading" | "loaded" | "failed" | "empty",
        selectedTypes: [] as MunicipalityType[],
        krajPublicId: "",
    };
    const classes = useStyles();
    const [state, setState] = React.useState(initState);
    const [krajs, setKrajs] = React.useState<Array<{publicId: string; name: string}>>([]);
    const searchedString = useRef("");

    React.useEffect(() => {
        if (!initialized) return;
        API.get<Page<MunicipalityListItemDTO>>(
            `/organizations/search?name=&types=KRAJ&size=100`,
            createApiConfig(keycloak, initialized)
        ).then((res) => {
            setKrajs(res.data.content.map((m) => ({publicId: m.publicId, name: m.name})));
        });
    }, [initialized, keycloak]);

    React.useEffect(() => {
        if (state.name === "loading") {
            searchedString.current = state.searchString.trim();
            const typesParam =
                state.selectedTypes.length > 0 ? state.selectedTypes.join(",") : municipalityTypes.join(",");
            const parentParam = state.krajPublicId ? `&parentPublicId=${encodeURIComponent(state.krajPublicId)}` : "";
            API.get<Page<MunicipalityListItemDTO>>(
                `/organizations/search?name=${encodeURIComponent(
                    state.searchString.trim()
                )}&types=${typesParam}${parentParam}${
                    state.orderBy ? "&sorted=" + state.orderBy + "&order=" + state.orderDirection : ""
                }&page=${state.page.number}&size=${state.page.size}`,
                createApiConfig(keycloak, initialized)
            )
                .then((res) => {
                    if (searchedString.current != state.searchString.trim()) return;
                    if (!res.data) return setState({...state, name: "empty"});
                    setState({
                        ...state,
                        page: res.data,
                        municipalities: res.data.content,
                        name: "loaded",
                    });
                })
                .catch((err: AxiosError) => {
                    setState({...state, name: "failed"});
                });
        }
    }, [keycloak, initialized, state, setState]);

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number): void => {
        setState({
            ...state,
            page: {...state.page, number: value - 1},
            name: "loading",
        });
    };
    const handleSort = (property: SortableProperties) => {
        setState({
            ...state,
            orderBy: property,
            orderDirection:
                (state.orderBy === property && state.orderDirection === "asc") ||
                (state.orderBy !== property && property !== null && propertiesWithDefaultDesc.includes(property))
                    ? "desc"
                    : "asc",
            name: "loading",
        });
    };
    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            name: "loading",
            searchString: e.target.value,
            orderBy: state.orderBy === "active" ? "name" : state.orderBy,
            orderDirection: state.orderBy === "active" ? "asc" : state.orderDirection,
        });
    };

    const handleTypeToggle = (type: MunicipalityType) => {
        const newTypes = state.selectedTypes.includes(type)
            ? state.selectedTypes.filter((t) => t !== type)
            : [...state.selectedTypes, type];
        setState({
            ...state,
            selectedTypes: newTypes,
            page: {...state.page, number: 0},
            name: "loading",
        });
    };

    const handleKrajChange = (e: React.ChangeEvent<{value: unknown}>) => {
        setState({
            ...state,
            krajPublicId: e.target.value as string,
            page: {...state.page, number: 0},
            name: "loading",
        });
    };

    const content = () => {
        if (state.name === "loading") {
            return <Loading />;
        }
        if (state.name === "failed") {
            return <ServerCommunicationAlert />;
        }
        if (state.name === "empty") {
            return <Alert severity="warning">{t("notifications.search_empty")}</Alert>;
        }
        const defaultHeaderProperties = {
            orderedBy: state.orderBy,
            orderedDirection: state.orderDirection,
            handler: handleSort,
            classOfHidden: classes.visuallyHidden,
        };
        return (
            <>
                <Paper className={classes.tableContainer} elevation={0}>
                    <Table>
                        <TableHead style={{backgroundColor: "#F9FAFB"}}>
                            <TableRow>
                                <TableCell style={{width: "56px"}}>{t("organization.logo")}</TableCell>
                                <SortableHeader
                                    label={t("organization.name")}
                                    property="name"
                                    {...defaultHeaderProperties}
                                />
                                <SortableHeader
                                    label={t("organization.municipalityTypeShort")}
                                    property="type"
                                    {...defaultHeaderProperties}
                                />
                                <SortableHeader
                                    label={t("type.COUNTY")}
                                    property="county_cz"
                                    {...defaultHeaderProperties}
                                />
                                <SortableHeader
                                    label={t("type.REGION")}
                                    property="region_cz"
                                    {...defaultHeaderProperties}
                                />
                                <SortableHeader
                                    label={t("organization.population")}
                                    property="population"
                                    align="right"
                                    {...defaultHeaderProperties}
                                />
                                <SortableHeader
                                    label={t("organization.area")}
                                    property="area"
                                    align="right"
                                    {...defaultHeaderProperties}
                                />
                                <TableCell style={{width: "40px"}} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {state.municipalities.map((municipality: MunicipalityListItemDTO) => (
                                <Municipality
                                    key={municipality.publicId}
                                    municipality={municipality}
                                    onClick={() => history.push(ROUTES.organization.create(municipality.publicId))}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
                {state.page.totalPages > 1 && (
                    <Box style={{display: "flex", justifyContent: "center", marginTop: "1.5rem"}}>
                        <Pagination
                            count={state.page.totalPages}
                            page={state.page.number + 1}
                            onChange={handlePageChange}
                            shape="rounded"
                            color="primary"
                        />
                    </Box>
                )}
            </>
        );
    };

    return (
        <Container maxWidth="lg">
            <ManualSidebar section={csPublicSearchSection} />
            <Box className={classes.searchWrapper}>
                <TextField
                    placeholder={t("ui.search")}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon style={{color: "#9CA3AF"}} />
                            </InputAdornment>
                        ),
                        className: classes.searchInput,
                    }}
                    variant="outlined"
                    fullWidth
                    onChange={handleSearch}
                    style={{maxWidth: "480px"}}
                />
            </Box>
            <Box className={classes.filterWrapper}>
                <Box className={classes.filterGroup}>
                    <Typography className={classes.filterLabel}>{t("organization.filterByStatus")}:</Typography>
                    {(municipalityTypes as MunicipalityType[]).map((type) => (
                        <Chip
                            key={type}
                            label={t("municipalityType." + type)}
                            onClick={() => handleTypeToggle(type)}
                            className={
                                state.selectedTypes.length === 0
                                    ? classes.defaultTypeChip
                                    : state.selectedTypes.includes(type)
                                    ? classes.activeTypeChip
                                    : classes.inactiveTypeChip
                            }
                            size="small"
                        />
                    ))}
                </Box>
                {krajs.length > 0 && (
                    <Box className={classes.filterGroup}>
                        <Typography className={classes.filterLabel}>{t("organization.filterByKraj")}:</Typography>
                        <FormControl variant="outlined" size="small" className={classes.krajSelect}>
                            <Select value={state.krajPublicId} onChange={handleKrajChange} displayEmpty>
                                <MenuItem value="">{t("organization.allKrajs")}</MenuItem>
                                {krajs.map((kraj) => (
                                    <MenuItem key={kraj.publicId} value={kraj.publicId}>
                                        {kraj.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                )}
            </Box>
            {content()}
        </Container>
    );
};

export default Municipalities;
